// src/components/CardStack3D.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import Lottie from 'lottie-react';
import {
    CARD_STACK_ANIMATION_BY_KEY,
    CARD_STACK_ANIMATION_FALLBACK_KEY,
    CARD_STACK_KEYWORD_RULES,
    type CardStackAnimationKey,
} from './cardStackAnimationConfig';


/** Shared types (unchanged) */
export type Plan = {
    id: string;
    name: string;
    price: number;
    cadence: '/month' | '/quarter' | '/year';
    billed?: string;
    blurb?: string;                  // fallback for description
    description?: string;            // short one-liner on the card
    accent?: string;
    bg?: string;                     // CSS gradient / color for the card face
    ink?: string;                    // text (ink) color on the card
    animationKey?: CardStackAnimationKey; // explicit Lottie selection
    animationOffsetY?: string;       // optional vertical offset for card animation placement
    features: string[];              // not shown on the card; the table uses this
    offerings: { key: string; label: string; value: string }[];
    popular?: boolean;
};

/** Props */
type CardStack3DProps = {
    plans: Plan[];
    initialIndex?: number;
    onChange?: (plan: Plan, index: number) => void;
    onCardClick?: (plan: Plan, index: number) => void;
    className?: string;
    hidePrice?: boolean;
    centerOnMobile?: boolean;
};


// NEW: choose animation by id/name (supports your older ids too)
function animationForPlan(p: Plan) {
    if (p.animationKey) {
        return CARD_STACK_ANIMATION_BY_KEY[p.animationKey];
    }

    const idSlug = (p.id || '').toLowerCase();
    const nameSlug = (p.name || '').toLowerCase();
    const key = `${idSlug} ${nameSlug}`;

    for (const rule of CARD_STACK_KEYWORD_RULES) {
        if (rule.pattern.test(key)) {
            return CARD_STACK_ANIMATION_BY_KEY[rule.key];
        }
    }

    return CARD_STACK_ANIMATION_BY_KEY[CARD_STACK_ANIMATION_FALLBACK_KEY];
}



/* ──────────────────────────────────────────────────────────
   Tunables (shape + stack)
────────────────────────────────────────────────────────── */
const CARD_RATIO = 3 / 4; // width / height (wider, like your mock)
const FRAME_MAX_W = 300;    // px – cap the card width
const FRAME_W_PCT = 0.92;   // % of the shell
const BOTTOM_GAP = 92;      // space for controls

// Stack transforms for offsets: -1 (left/back), 0 (front), +1 (right/back)
// If more than 3 plans, off-screen ones get clamped to ±2 with heavier pushback.
function transformFor(offset: number) {
    const o = Math.max(-2, Math.min(2, offset));
    if (o === 0) {
        return { tx: 0, ty: 0, tz: 0, rz: 0, scale: 1.0, z: 40 };
    }
    if (o === -1) {
        return { tx: -28, ty: 18, tz: -80, rz: -12, scale: 0.94, z: 30 };
    }
    if (o === 1) {
        return { tx: 28, ty: 26, tz: -120, rz: 12, scale: 0.88, z: 20 };
    }
    // far back (±2)
    return {
        tx: o < 0 ? -42 : 42,
        ty: 38,
        tz: -180,
        rz: o < 0 ? -16 : 16,
        scale: 0.82,
        z: 10,
    };
}

/* Default themes if bg/ink not provided on a plan */
function defaultThemeForPlan(p: Plan) {
    // Use BOTH id and name (the old code used id OR name)
    const idSlug = (p.id || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const nameSlug = (p.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const key = `${idSlug} ${nameSlug}`;

    // Map your existing ids/names as well as the marketing/digital/ecommerce variants
    const isMarketing = /marketing|monthly/.test(key);
    const isEcom = /ecom|commerce|annual|scale/.test(key);      // check ecommerce BEFORE digital
    const isDigital = /digital|quarterly|studio/.test(key);

    if (isMarketing) {
        return { bg: 'linear-gradient(135deg, #ff6a00 0%, #ff345b 50%, #ff0088 100%)', ink: '#ffffff' };
    }
    if (isEcom) {
        return { bg: 'linear-gradient(135deg, #ffe259 0%, #ffb300 55%, #ff8f00 100%)', ink: '#1a1400' };
    }
    if (isDigital) {
        return { bg: 'linear-gradient(135deg, #2fa8ff 0%, #05d6a0 100%)', ink: '#ffffff' };
    }
    return { bg: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)', ink: '#ffffff' };
}




/* ──────────────────────────────────────────────────────────
   Shell – same footprint as the old Panel, but neutral
────────────────────────────────────────────────────────── */
const StackShell = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 24px;
  padding: 1.25rem 1rem ${BOTTOM_GAP}px; /* smaller top pad; room at bottom for controls */
  position: relative;
  background: transparent;        /* the cards bring color */

  transform-style: preserve-3d;
  perspective: 1200px;
  min-height: 580px;

  @media (max-width: 900px) {
    min-height: 480px;
  }

  @media (max-width: 680px) {
    min-height: 390px;
    padding: 0.7rem 0.65rem 74px;
    border-radius: 18px;
  }
`;

/* This centers a fixed-aspect-ratio "frame" inside the shell.
   Cards are absolutely positioned to this frame and scaled/rotated. */
const Viewport = styled.div<{ $centerOnMobile: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: ${({ $centerOnMobile }) => ($centerOnMobile ? 'center' : 'start')};
  padding: 1rem 1rem 0.25rem;

  @media (min-width: 900px) {
    place-items: center;
  }

  @media (max-width: 680px) {
    padding: 0.45rem 0.45rem 0.15rem;
  }
`;

const Frame = styled.div`
  width: min(${FRAME_W_PCT * 100}%, ${FRAME_MAX_W}px);
  aspect-ratio: ${CARD_RATIO};
  position: relative;
  /* Let rotated cards breathe; still clipped by the shell itself */
  overflow: visible;

  @media (max-width: 680px) {
    width: min(96%, 260px);
  }
`;

/* Controls */
const Controls = styled.div`
  position: absolute;
  bottom: 1.0rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: .5rem;
  z-index: 200;

  @media (max-width: 680px) {
    bottom: 0.65rem;
    gap: 0.4rem;
  }
`;

const ControlBtn = styled.button`
  background: #161616;
  color: #fff;
  border: 1px solid #2e2e2e;
  padding: .55rem .8rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: .85rem;
  transition: transform .15s ease, background .15s ease;
  &:hover { transform: translateY(-1px); background: #1d1d1d; }

  @media (max-width: 680px) {
    padding: 0.44rem 0.6rem;
    font-size: 0.78rem;
  }
`;

/* Card */
const Card = styled.article<{
    offset: number;
    clickable: boolean;
    bg: string;
    ink: string;
}>`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  overflow: hidden;

  /* Paper look */
  background: ${({ bg }) => bg};
  color: ${({ ink }) => ink};
//   border: 1px solid rgba(255, 255, 255, .10);
  box-shadow:
    0 28px 58px rgba(0,0,0,.25),
    0 10px 22px rgba(0,0,0,.20);

  /* bevel & gloss */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(255,255,255,.08), transparent 40%),
      radial-gradient(110% 150% at 110% 110%, rgba(255,255,255,.12), transparent 50%);
    pointer-events: none;
    z-index: 0; 
  }

  /* soft noise to avoid banding */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    opacity: .06;
    mix-blend-mode: overlay;
    background-image:
      radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,.9) 0, transparent 100%),
      radial-gradient(1px 1px at 70% 60%, rgba(0,0,0,.6) 0, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }

  transform-origin: 50% 78%; /* pivot near the bottom like a real stack */

  ${({ offset }) => {
        const t = transformFor(offset);
        return css`
      transform:
        translate3d(${t.tx}px, ${t.ty}px, ${t.tz}px)
        rotateZ(${t.rz}deg)
        scale(${t.scale});
      z-index: ${t.z};
      transition: transform 420ms cubic-bezier(.22,.61,.36,1),
                  box-shadow 420ms ease;
    `;
    }}

  ${({ clickable }) => clickable && css`
    cursor: pointer;
    &:hover { box-shadow: 0 32px 64px rgba(0,0,0,.30), 0 10px 24px rgba(0,0,0,.22); }
  `}

  @media (max-width: 680px) {
    border-radius: 20px;
  }
`;

/* Card content – anchored to bottom-left, like the reference */
const CardBody = styled.div`
  position: absolute;
  left: 1.75rem;
  right: 1.75rem;
  bottom: 1.6rem;
  z-index: 2; 

  @media (max-width: 680px) {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
  }
`;

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  margin: 0 0 .35rem;
`;

const Title = styled.h4<{ $accent?: string }>`
  margin: 0;
  font-weight: 800;
  letter-spacing: .2px;
  font-size: 1.35rem;
  color: ${({ $accent }) => $accent ?? 'currentColor'};

  @media (max-width: 680px) {
    font-size: 1.08rem;
  }
`;

const Popular = styled.span`
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  padding: .26rem .5rem;
  border-radius: 9999px;
  color: #000;
  background: rgba(255,255,255,.92);
`;

const Price = styled.div`
  font-weight: 900;
  font-size: 3rem;
  line-height: 1;
  margin: 0 0 .35rem 0;
  span {
    font-size: .75rem;
    font-weight: 600;
    opacity: .9;
    margin-left: .35rem;
  }

  @media (max-width: 680px) {
    font-size: 2.15rem;
  }
`;

const SubLine = styled.div`
  font-size: 1rem;
  font-weight: 600;
  opacity: .92;

  @media (max-width: 680px) {
    font-size: 0.9rem;
  }
`;

const TinyMuted = styled.div`
  opacity: .75;
  font-size: .85rem;
  margin-top: .15rem;

  @media (max-width: 680px) {
    font-size: 0.78rem;
  }
`;


// NEW: Lottie container (sits in top half of the card)
const LottieWrap = styled.div<{ $offsetY?: string }>`
  position: absolute;
  top: ${({ $offsetY }) => $offsetY ?? '8%'};
  left: 8%;
  right: 8%;
  height: 46%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;   /* clicks go through */
  z-index: 1;             /* under text, above gloss */
`;


/* ────────────────────────────────────────────────────────── */

export default function CardStack3D({
    plans,
    initialIndex = 0,
    onChange,
    onCardClick,
    className,
    hidePrice = false,
    centerOnMobile = false,
}: CardStack3DProps) {
    const [index, setIndex] = useState(Math.max(0, Math.min(plans.length - 1, initialIndex)));

    const go = useCallback((dir: 1 | -1) => {
        setIndex(i => (i + dir + plans.length) % plans.length);
    }, [plans.length]);

    const bringToFront = useCallback((i: number) => setIndex(i), []);

    const activePlan = plans[index];

    useEffect(() => {
        if (onChange && activePlan) onChange(activePlan, index);
    }, [index]); // eslint-disable-line

    // Keyboard nav
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') go(1);
            if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go]);

    const ordered = useMemo(() => plans.map((_, i) => i), [plans]);

    return (
        <StackShell className={className} role="region" aria-label="Plans carousel">
            <Viewport $centerOnMobile={centerOnMobile}>
                <Frame>
                    {ordered.map((i) => {
                        // relative offset to the active card (wrap for circular order)
                        const raw = i - index;
                        const offset =
                            Math.abs(raw) <= plans.length / 2
                                ? raw
                                : raw > 0
                                    ? raw - plans.length
                                    : raw + plans.length;

                        const p = plans[i];
                        const theme = {
                            bg: p.bg ?? defaultThemeForPlan(p).bg,
                            ink: p.ink ?? defaultThemeForPlan(p).ink,
                        };
                        const isActive = offset === 0;
                        const animData = animationForPlan(p);
                        const handleCardClick = () => {
                            if (onCardClick) {
                                onCardClick(p, i);
                                return;
                            }
                            if (offset !== 0) {
                                bringToFront(i);
                            }
                        };

                        return (
                            <Card
                                key={p.id}
                                offset={offset}
                                clickable={offset !== 0 || Boolean(onCardClick)}
                                bg={theme.bg}
                                ink={theme.ink}
                                onClick={handleCardClick}
                                aria-label={`${p.name} plan`}
                            >
                                <LottieWrap aria-hidden="true" $offsetY={p.animationOffsetY}>
                                    <Lottie
                                        animationData={animData}
                                        loop={isActive}          // only loop the front card
                                        autoplay={isActive}      // start when it becomes active
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </LottieWrap>
                                <CardBody>
                                    <TitleRow>
                                        <Title $accent={p.accent}>{p.name}</Title>
                                        {p.popular && <Popular>Popular</Popular>}
                                    </TitleRow>

                                    {!hidePrice ? (
                                        <Price>
                                            ${p.price.toLocaleString()}
                                            <span>{p.cadence}</span>
                                        </Price>
                                    ) : null}

                                    {(p.description || p.blurb) && (
                                        <SubLine>{p.description || p.blurb}</SubLine>
                                    )}
                                    {p.billed && <TinyMuted>{p.billed}</TinyMuted>}
                                </CardBody>
                            </Card>
                        );
                    })}
                </Frame>
            </Viewport>

            <Controls>
                <ControlBtn onClick={() => go(-1)} aria-label="Previous plan">← Prev</ControlBtn>
                <ControlBtn onClick={() => go(1)} aria-label="Next plan">Next →</ControlBtn>
            </Controls>
        </StackShell>
    );
}
